import React, { useMemo, useState } from "react";
import { App, Button, Card, Col, Input, Row, Space, Typography } from "antd";
import { CopyOutlined, SwapOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { PublicLayout } from "../shared/components/layout";
import { useAntdTheme } from "../shared/hooks";
import {
  TridConversionError,
  type TridConversionErrorCode,
  tridToUuid,
  uuidToTrid,
} from "../shared/utils/trid";

const { Paragraph, Text, Title } = Typography;

interface ConversionResult {
  value: string;
  errorCode?: TridConversionErrorCode;
}

const getErrorCode = (error: unknown): TridConversionErrorCode => {
  if (error instanceof TridConversionError) {
    return error.code;
  }

  return "invalid_trid_payload";
};

const TridConverterPage: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { token } = useAntdTheme();
  const [uuid, setUuid] = useState("");
  const [tag, setTag] = useState("its");
  const [trid, setTrid] = useState("");

  const tridResult = useMemo<ConversionResult>(() => {
    if (!uuid.trim()) {
      return { value: "" };
    }

    try {
      return { value: uuidToTrid(uuid, tag) };
    } catch (error) {
      return { value: "", errorCode: getErrorCode(error) };
    }
  }, [tag, uuid]);

  const uuidResult = useMemo<ConversionResult>(() => {
    if (!trid.trim()) {
      return { value: "" };
    }

    try {
      const result = tridToUuid(trid);
      return { value: result.uuid };
    } catch (error) {
      return { value: "", errorCode: getErrorCode(error) };
    }
  }, [trid]);

  const decodedTag = useMemo(() => {
    if (!trid.trim()) {
      return "";
    }

    try {
      return tridToUuid(trid).tag;
    } catch {
      return "";
    }
  }, [trid]);

  const handleCopy = async (value: string) => {
    await navigator.clipboard.writeText(value);
    message.success(t("trid_converter_copied"));
  };

  const renderCopyButton = (value: string, label: string) => (
    <Button
      icon={<CopyOutlined />}
      disabled={!value}
      aria-label={label}
      onClick={() => handleCopy(value)}
    />
  );

  return (
    <PublicLayout>
      <main
        style={{
          minHeight: "100vh",
          background: `linear-gradient(145deg, ${token.colorPrimaryBg} 0%, ${token.colorBgLayout} 55%, ${token.colorInfoBg} 100%)`,
          padding: `${token.paddingXL * 2}px ${token.paddingLG}px`,
        }}
      >
        <div style={{ width: "100%", maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: token.marginXL }}>
            <SwapOutlined
              style={{
                color: token.colorPrimary,
                fontSize: token.fontSizeHeading1,
                marginBottom: token.marginMD,
              }}
            />
            <Title level={1} style={{ marginBottom: token.marginXS }}>
              {t("trid_converter_title")}
            </Title>
            <Paragraph type="secondary" style={{ fontSize: token.fontSizeLG }}>
              {t("trid_converter_description")}
            </Paragraph>
          </div>

          <Row gutter={[token.marginLG, token.marginLG]}>
            <Col xs={24} lg={12}>
              <Card
                title={t("trid_converter_uuid_to_trid")}
                style={{ height: "100%", boxShadow: token.boxShadowSecondary }}
              >
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  <div>
                    <Text strong>{t("trid_converter_uuid")}</Text>
                    <Input
                      value={uuid}
                      status={tridResult.errorCode === "invalid_uuid" ? "error" : undefined}
                      placeholder={t("trid_converter_uuid_placeholder")}
                      onChange={(event) => setUuid(event.target.value)}
                      style={{ marginTop: token.marginXS, fontFamily: "monospace" }}
                    />
                    {tridResult.errorCode === "invalid_uuid" && (
                      <Text type="danger">{t("trid_converter_invalid_uuid")}</Text>
                    )}
                  </div>

                  <div>
                    <Text strong>{t("trid_converter_tag")}</Text>
                    <Input
                      value={tag}
                      maxLength={3}
                      status={tridResult.errorCode === "invalid_tag" ? "error" : undefined}
                      placeholder={t("trid_converter_tag_placeholder")}
                      onChange={(event) => setTag(event.target.value.toLowerCase())}
                      style={{ marginTop: token.marginXS, fontFamily: "monospace" }}
                    />
                    <div>
                      <Text type={tridResult.errorCode === "invalid_tag" ? "danger" : "secondary"}>
                        {tridResult.errorCode === "invalid_tag"
                          ? t("trid_converter_invalid_tag")
                          : t("trid_converter_tag_help")}
                      </Text>
                    </div>
                  </div>

                  <div>
                    <Text strong>{t("trid_converter_trid")}</Text>
                    <Space.Compact style={{ width: "100%", marginTop: token.marginXS }}>
                      <Input
                        readOnly
                        value={tridResult.value}
                        placeholder={t("trid_converter_result_placeholder")}
                        aria-label={t("trid_converter_trid_result")}
                        style={{ fontFamily: "monospace" }}
                      />
                      {renderCopyButton(tridResult.value, t("trid_converter_copy_trid"))}
                    </Space.Compact>
                  </div>
                </Space>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card
                title={t("trid_converter_trid_to_uuid")}
                style={{ height: "100%", boxShadow: token.boxShadowSecondary }}
              >
                <Space direction="vertical" size="large" style={{ width: "100%" }}>
                  <div>
                    <Text strong>{t("trid_converter_trid")}</Text>
                    <Input
                      value={trid}
                      status={uuidResult.errorCode ? "error" : undefined}
                      placeholder={t("trid_converter_trid_placeholder")}
                      onChange={(event) => setTrid(event.target.value)}
                      style={{ marginTop: token.marginXS, fontFamily: "monospace" }}
                    />
                    {uuidResult.errorCode && (
                      <Text type="danger">
                        {t(
                          uuidResult.errorCode === "invalid_trid"
                            ? "trid_converter_invalid_trid"
                            : "trid_converter_invalid_trid_payload"
                        )}
                      </Text>
                    )}
                  </div>

                  <div>
                    <Text strong>{t("trid_converter_uuid")}</Text>
                    <Space.Compact style={{ width: "100%", marginTop: token.marginXS }}>
                      <Input
                        readOnly
                        value={uuidResult.value}
                        placeholder={t("trid_converter_result_placeholder")}
                        aria-label={t("trid_converter_uuid_result")}
                        style={{ fontFamily: "monospace" }}
                      />
                      {renderCopyButton(uuidResult.value, t("trid_converter_copy_uuid"))}
                    </Space.Compact>
                  </div>

                  <div>
                    <Text strong>{t("trid_converter_tag")}</Text>
                    <Input
                      readOnly
                      value={decodedTag}
                      placeholder={t("trid_converter_result_placeholder")}
                      aria-label={t("trid_converter_tag_result")}
                      style={{
                        marginTop: token.marginXS,
                        fontFamily: "monospace",
                        maxWidth: 120,
                      }}
                    />
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          <Paragraph
            type="secondary"
            style={{ textAlign: "center", marginTop: token.marginXL, marginBottom: 0 }}
          >
            {t("trid_converter_local_only")}
          </Paragraph>
        </div>
      </main>
    </PublicLayout>
  );
};

export default TridConverterPage;
